/* eslint-disable max-len */
import styled from 'styled-components';
import classNames from 'classnames';
import { Company } from 'Types/CompaniesTypes';
import { SelectedCompany } from 'Types/GlobalSearchResultsTypes';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/CompaniesSlice';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumberWithCommas, valueToObject } from 'Utils/UtilityFunctions';
import HeadingWithShowAll from 'Components/globalSearch/components/HeadingWithShowAll';
import SectionHeading from 'Components/globalSearch/components/SectionHeading';
import { Checkbox, CheckboxChangeEvent } from 'antd';
import DrawerPeopleMoveReason from 'Components/people/components/DrawerPeopleMoveReason';
import { COMPANY_OVERVIEW_ROUTE, FEED_GILROY_ROUTE, SEARCH_ROUTE } from 'Utils/Constants';
import { selectIsPagination, selectSelectedCompanies } from 'Selector/GlobalSearchResultsSelector';
import { actions as globalSearchActions } from 'Slice/GlobalSearchResultsSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import CompanyFollowUnFollowButton from './CompanyFollowUnfollowButton';
import LazyTooltip, { LazyTooltipBoundary } from './LazyTooltip';
import {
  primaryColor, primaryFont, wMedium,
  maxWidth, retina, maxTablet, maxMobile,
  textBlack,
  wMLight,
  SecondaryColor,
  minExtraLargeScreen,
} from '../../../Utils/utilsStyle';
import CompanyWidgetLarge from './CompanyWidgetLarge';
import SmartPlanSvg from '../assets/svgIcons/SmartPlanSvg';
import CompanyListViewSkeleton from '../skeleton/CompanyListViewSkeleton';

type Props = {
  listView?: boolean,
  heading?: string,
  companiesCollection?: Company[],
  loading?: boolean,
  companyCount?: Company[],
  count?: number,
  showDrawerIcon?: boolean
  showButton?: boolean
  removeButton?: boolean,
  isSearch?: boolean,
  isTableCompanyCompare?: boolean,
  disableNavigation?: boolean
  inGlobalSearch?: boolean,
  inAdvanceSearch?: boolean,
  query?: string,
  hideShowAll?: boolean,
  isPaginationOverride?: boolean,
  forceVisible?: boolean,
} & typeof defaultProps;

const defaultProps = {
  listView: false,
  heading: '',
  loading: false,
  companiesCollection: [] as Company[],
  companyCount: [] as Company[],
  count: 0,
  showDrawerIcon: false,
  showButton: true,
  removeButton: false,
  isSearch: false,
  isTableCompanyCompare: false,
  disableNavigation: false,
  inGlobalSearch: false,
  inAdvanceSearch: false,
  query: '',
  hideShowAll: false,
  forceVisible: false,
};

const EmptyResults = styled.span`
  padding: 20px;
  min-height: 250px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  line-height: 16px;
  color: rgb(126, 126, 126);
  font-family: ${primaryFont};
`;

const StyleWrapper = styled.div`
&.extendedSearch {
  margin-bottom: 16px;
  @media all and (min-width: ${minExtraLargeScreen}) {
    margin-bottom: 24px;
  }
}
.companies-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0px;
  width: 100%;
  cursor: pointer;
  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 10px 0px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    padding: 6px 0px;
  }
  @media all and (max-width: ${maxTablet}) {
    padding: 4px 0px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 4px 0px;
  }
  &:hover {
    background: #FFF;
    border-radius: 6px;
  }
  .company-checkbox {
    margin-right: 12px;
  }
  .company-info {  
    display: flex;
    column-gap: 10px;
    align-items: center;
    width: calc(100% - 84px);
    @media all and (min-width: ${minExtraLargeScreen}) {
      column-gap: 14px;
    }
    @media all and (max-width: ${maxWidth}), ${retina} {
      column-gap: 10px;
    }
    @media all and (max-width: ${maxTablet}) {
      column-gap: 6px;
    }
    @media all and (max-width: ${maxMobile}) {
      column-gap: 6px;
    }
    .company-image {
      width: 48px;
      height: 42px;
      @media all and (min-width: ${minExtraLargeScreen}) {
        width: 62px;
        height: 54px;
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        width: 48px;
        height: 42px;
      }
      @media all and (max-width: ${maxTablet}) {
        width: 38px;
        height: 34px;
      }
      @media all and (max-width: ${maxMobile}) {
        width: 34px;
        height: 30px;
      }
    }
    .company-dets{
      width: calc(100% - 48px);
      display: flex;
      flex-direction: column;
      @media all and (min-width: ${minExtraLargeScreen}) {
        width: calc(100% - 62px);
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        width: calc(100% - 48px);
      }
      @media all and (max-width: ${maxTablet}) {
        width: calc(100% - 38px);
      }
      @media all and (max-width: ${maxMobile}) {
        width: calc(100% - 32px);
      }
      .person-designation-tip {
        @media all and (max-width: ${maxTablet}) {
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          word-wrap: break-word;
        }
      }
      .companyName{
        color: ${textBlack};
        font-family: ${primaryFont};
        font-size: 16px;
        font-weight: ${wMLight};
        line-height: 18px;
        letter-spacing: -0.52px;
        margin-bottom: 4px;
        max-width: calc(100% -80px);
        cursor: pointer;
        display: inline-block;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        &.triggers-bullet-tooltip {
          &.person-designation-tip {
            .ant-tooltip {
              .ant-tooltip-content {
                .ant-tooltip-inner {
                  white-space: normal;
                }
              }
            }
          }
        }
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 22px;
          line-height: 23px;
          letter-spacing: -0.99px;
          margin-bottom: 8px;
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          font-size: 16px;
          font-weight: ${wMLight};
          line-height: 18px;
          letter-spacing: -0.52px;
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.336px;
          margin-bottom: 0px;
        }
        @media all and (max-width: ${maxMobile}) {
          margin-left: 0px;
          font-size: 12px;
        } 
      }
      .company-web-wrapper {
        display: flex;
        align-items: center;
        column-gap: 12px;
        cursor: pointer;
        @media all and (min-width: ${minExtraLargeScreen}) {
          column-gap: 16px;
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          column-gap: 14px;
        }
        @media all and (max-width: ${maxTablet}) {
          column-gap: 8px;
        }
        @media all and (max-width: ${maxMobile}) {
          column-gap: 12px;
          margin-top: -1px;
        }
        .company-website-link-wrapper {
          display: flex;
          .company-website{
            span{
              margin: 0px;
              color: ${primaryColor};
              font-family: ${primaryFont};
              font-size: 14px;
              font-weight: ${wMedium};
              line-height: 16px;
              letter-spacing: -0.424px;
              @media all and (min-width: ${minExtraLargeScreen}) {
                font-size: 20px;
                letter-spacing: -0.599px;
                line-height: 21px;
                margin-top: 4px;
              }
              @media all and (max-width: ${maxWidth}), ${retina} {
                font-size: 14px;
                line-height: 16px;
                letter-spacing: -0.424px;
              }
              @media all and (max-width: ${maxTablet}) {
                font-size: 12px;
                line-height: 14px;
                letter-spacing: -0.274px;
              }
              @media all and (max-width: ${maxMobile}) {       
                font-size: 10px;
                line-height: 12px;
                letter-spacing: -0.195px;
              }
            }
          }
        }
        .smart-drawer {
          position: relative;
          .messageIcon {
            width: 20px;
            height: 20px;
            @media all and (max-width: ${maxWidth}), ${retina} {
              width: 16px;
              height: 16px;
            }
            @media all and (max-width: ${maxTablet}) {
              width: 14px;
              height: 14px;
            }
            @media all and (max-width: ${maxMobile}) {
              width: 18px;
              height: 18px;
            }
          }
          svg {
            left: 4px;
            top: 4px;
            @media all and (min-width: ${minExtraLargeScreen}) {
              left: 3px;
              top: 4px;
            }
            @media all and (max-width: ${maxWidth}), ${retina} {
              top: 4px;
              left: 3px;
              width: 10px;
              height: 10px;
            }
            @media all and (max-width: ${maxTablet}) {
              top: 3px;
              left: 3px;
              width: 8px;
              height: 8px;
            }
            @media all and (max-width: ${maxMobile}) {
              width: 11px;
              height: 11px;
              top: 4px;
              left: 4px;
            }
          }
          &::before {
            content: "";
            background-color: rgb(207, 207, 207);
            position: absolute;
            min-width: 1px;
            min-height: 11px;
            left: -7px;
            top: 4px;
            @media all and (min-width: ${minExtraLargeScreen}) {
              min-height: 15px;
              left: -8px;
              top: 4px;
            }
            @media all and (max-width: ${maxWidth}), ${retina} {
              min-height: 11px;
              left: -7px;
              top: 3px;
            }
            @media all and (max-width: ${maxTablet}) {
              min-height: 8px;
              left: -4px;
              top: 4px;
            }
            @media all and (max-width: ${maxMobile}) {
              min-height: 10px;
              top: 5px;
              left: -6px;
            }
          }
        }
        .smart-account{
          position: relative;
          &::before {
            content: "";
            min-width: 1px;
            min-height: 11px;
            left: -7px;
            top: 5px;
            background-color: rgb(207, 207, 207);
            position: absolute;
            @media all and (min-width: ${minExtraLargeScreen}) {
              min-height: 15px;
              left: -8px;
              top: 4px;
            }
            @media all and (max-width: ${maxWidth}), ${retina} {
              min-height: 11px;
              left: -7px;
              top: 3px;
            }
            @media all and (max-width: ${maxTablet}) {
              min-height: 8px;
              left: -5px;
              top: 4px;
            }
            @media all and (max-width: ${maxMobile}) {
              min-height: 10px;
              left: -6px;
              top: 5px;
            }
          }
          .smart-account-plan-history-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background-color: ${SecondaryColor};
            cursor: pointer;
            @media all and (max-width: ${maxWidth}) , ${retina} {
              width: 16px;
              height: 16px;
            }
            @media all and (max-width: ${maxTablet}) {
              width: 14px;
              height: 14px;
            }
            @media all and (max-width: ${maxMobile}) {
              width: 18px;
              height: 18px;
            }
            svg {
              height:12px;
              margin: 0px;
              @media all and (min-width: ${minExtraLargeScreen}) {
                height:12px;
                margin: 0px;
              }
              @media all and (max-width: ${maxWidth}) , ${retina} {
                height: 11px;
                margin: 3px;
              }
              @media all and (max-width: ${maxTablet}) {
                height: 9px;
              }
              @media all and (max-width: ${maxMobile}) {
                height: 11px;
              }
            } 
          }
        }
      }
    }
  }
  .follow-action {
    .small-button-chinese {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 24px;
      width: 84px;
      padding: 4px 14px;
      border-radius: 16px;
      gap: 10px;
      flex-shrink: 0;
      text-align: center;
      font-family: ${primaryFont};
      font-size: 14px;
      font-weight: ${wMLight};
      line-height: 17px;
      @media all and (min-width: ${minExtraLargeScreen}) {
        height: 34px;
        width: 120px;
        padding: 6px 20px;
        border-radius: 22px;
        font-size: 20px;
        line-height: 23px;
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        height: 24px;
        width: 84px;
        font-size: 14px;
        line-height: 17px;
        padding: 4px 14px;
      }
      @media all and (max-width: ${maxTablet}) {
        height: 20px;
        width: 72px;
        padding: 3px 12px;
        font-size: 12px;
        line-height: 15px;
      }
      @media all and (max-width: ${maxMobile}) {
        height: 20px;
        width: 64px;
        font-size: 12px;
        line-height: 15px;
      }
    }
  }
  .actionTableBtn {
    button {
      padding: 5px 10px;
      font-size: 10px;
      margin: 10px 0px 0px 10px;
    }
  }
  &:last-child {
    margin-bottom: 0px;
  }
}
`;
const CompaniesSectionListView = function CompaniesSectionListView(props: Props) {
  const [searchParams] = useSearchParams();
  const extendedSections = ['person', 'company', 'article'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const isPagination: boolean = useSelector(selectIsPagination);
  const {
    heading, companiesCollection, loading,
    companyCount,
    count,
    showDrawerIcon,
    showButton,
    removeButton,
    isSearch,
    isTableCompanyCompare,
    listView,
    disableNavigation,
    inGlobalSearch,
    inAdvanceSearch,
    query,
    hideShowAll,
    isPaginationOverride,
    forceVisible,
  } = props;
  const paginationActive = typeof isPaginationOverride === 'boolean'
    ? isPaginationOverride
    : isPagination;
  const shouldShowPaginationSkeleton = paginationActive
    && (isDetailedSearch || typeof isPaginationOverride === 'boolean');
  const formattedCount = formatNumberWithCommas(count);
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const host = window.location.href;
  const selectedCompanies = useSelector(selectSelectedCompanies) as SelectedCompany[];
  const handleCardRightClicked = (companyId: number) => {
    dispatch(actions.setNewsFlurry({}));
    if (!removeButton && !disableNavigation) window.open(`${COMPANY_OVERVIEW_ROUTE}${companyId}, '_blank'`);
  };

  const handleCardClicked = (companyId: number) => {
    dispatch(actions.setNewsFlurry({}));
    if (!removeButton && !disableNavigation) navigate(`${COMPANY_OVERVIEW_ROUTE}${companyId}`);
  };

  const createCompaniesRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_companies_see_all_clicked', 0, {
        host,
      }),
    ));
    window.scrollTo(0, 0);
    return navigate(`${pathname}?query=${query}&extend=company`, { state: { enableBackButton: true, previousPath } });
  };

  const handleAccountPlanClick = (id: number) => {
    dispatch(actions.resetAccountPlan());
    dispatch(actions.setShowSAPHistory(true));
    navigate(`${COMPANY_OVERVIEW_ROUTE}${id}&accountPlan=true`);
  };

  const handleCompanyWebsite = (company: Company) => {
    window.open(company?.companyWebsite.slice(0, 4) === 'http'
      ? company?.companyWebsite : `https://${company?.companyWebsite}`);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('company_websiteLink_clicked', 0, {
        company_id: company?.companyId,
        company_name: company?.companyName,
        host,
      }),
    ));
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  const handleCompanyClick = ({ companyId, companyName }: Company) => {
    if ((isSearch || inGlobalSearch) && !disableNavigation) {
      dispatch(certificationActions.setSearchCompanyClicked(true));
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('searchPage_company_profile_clicked', 0, {
          company_id: companyId,
          company_name: companyName,
          host,
        }),
      ));
    }
  };
  const handleCheckboxChange = (company: Company, e?: CheckboxChangeEvent) => {
    if (e && (e as any).nativeEvent) (e as any).nativeEvent.stopPropagation();
    dispatch(globalSearchActions.toggleSelectedCompany({
      companyId: company?.companyId,
      companyName: company?.companyName,
      companySubscribed: !!company?.companySubscribed,
    }));
  };
  return (
    companiesCollection.length > 0 || loading || forceVisible ? (
      <StyleWrapper
        className={classNames({
          extendedSearch: isDetailedSearch,
        })}
      >
        {
          loading && !isDetailedSearch ? (
            <CompanyListViewSkeleton />
          ) : (
            <div>
              <HeadingWithShowAll
                inGlobalSearch={inGlobalSearch}
                isShowAllHidden={hideShowAll || (isDetailedSearch ? true
                  : !forceVisible && !!(companyCount && companyCount.length <= 4))}
                leftHeading={(
                  <SectionHeading customClassName="global-search-heading">{`${heading} (${formattedCount})`}</SectionHeading>)}
                onClick={createCompaniesRoute}
                onSeelessClick={navigateToSearchResult}
                isDetailedSearch={!hideShowAll && isDetailedSearch}
              />
              {
                companiesCollection?.map((company) => (
                  <LazyTooltipBoundary>
                    <div
                      className="companies-container"
                      onClick={() => handleCardClicked(company?.companyId)}
                      onKeyDown={() => handleCardClicked(company?.companyId)}
                      role="button"
                      tabIndex={0}
                    >
                      {
                        (isDetailedSearch && inGlobalSearch) || inAdvanceSearch
                          ? (
                            <span
                              role="presentation"
                              className="company-checkbox"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                id={`company-checkbox-${company?.companyId}`}
                                checked={selectedCompanies
                                  .some((item) => item.companyId === company?.companyId)}
                                className="checkbox-company-widget"
                                onChange={(e) => handleCheckboxChange(company, e)}
                                disabled={selectedCompanies.length >= 20 && !(selectedCompanies
                                  .some((item) => item.companyId === company?.companyId))}
                              />
                            </span>
                          )
                          : null
                      }
                      <div
                        className="company-info"
                        onClick={() => handleCompanyClick(company)}
                        role="button"
                        tabIndex={-1}
                      >
                        <div className="company-image">
                          <CompanyWidgetLarge
                            logo={company.companyLogo}
                            persona={company.companyPersonality}
                            triggerColor={company.companyPulse}
                            imagePosition="center 35%"
                            imageSize="50%"
                            imageSizeSm="35%"
                            width="48px"
                            height="42px"
                            heightXsm="42px"
                            widthXsm="48px"
                            widthSm="48px"
                            heightSm="42px"
                            widthXxsm="38px"
                            heightXxsm="34px"
                            showDrawerIcon={false}
                            showSapIcon={false}
                            id={company?.companyId}
                            handleCardClicked={() => handleCardRightClicked(company?.companyId)}
                            removeButton={removeButton}
                            listView={listView}
                            inGlobalSearch={inGlobalSearch}
                            handleImageClick={(id) => handleCardClicked(id)}
                          />
                        </div>
                        <div className="company-dets">
                          <LazyTooltip
                            trigger="hover"
                            placement="topLeft"
                            getPopupContainer={(e: HTMLElement) => e}
                            className="triggers-bullet-tooltip person-designation-tip"
                            color="#fff"
                            title={company.companyName}
                            arrow={false}
                          >
                            <span
                              className="companyName"
                              onClick={() => handleCardClicked(company?.companyId)}
                              onKeyDown={() => handleCardClicked(company?.companyId)}
                              role="button"
                              tabIndex={0}
                            >
                              {company?.companyName}
                            </span>
                          </LazyTooltip>
                          <div className="company-web-wrapper">
                            <div className="company-website-link-wrapper">
                              {company?.companyDomain
                                ? (
                                  <div className="company-website">
                                    <LazyTooltip
                                      trigger="hover"
                                      placement="top"
                                      getPopupContainer={(e: HTMLElement) => e}
                                      className="triggers-bullet-tooltip person-designation-tip"
                                      color="#fff"
                                      title={company.companyDomain}
                                      arrow={false}
                                    >
                                      <span>
                                        {company?.companyDomain}
                                      </span>
                                    </LazyTooltip>
                                  </div>
                                ) : null}
                            </div>
                            {
                              !!company.companyNotes && (
                                <div
                                  className="smart-drawer"
                                  role="button"
                                  tabIndex={0}
                                  aria-label="button"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyPress={(e) => e.stopPropagation()}
                                >
                                  <DrawerPeopleMoveReason companyId={company.companyId} showNotes isCompanyNotes />
                                </div>
                              )
                            }
                            {
                              company?.accountPlanAllowed ? (
                                <div
                                  className="smart-account"
                                  role="button"
                                  tabIndex={0}
                                  aria-label="button"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyPress={(e) => e.stopPropagation()}
                                >
                                  <LazyTooltip
                                    placement="top"
                                    getPopupContainer={(e: HTMLElement) => e}
                                    className="triggers-bullet-tooltip smartHistoryTip"
                                    color="#fff"
                                    title="View already created Smart Account Plans"
                                    arrow={false}
                                  >
                                    <div
                                      className={classNames('smart-account-plan-history-icon', { noNotes: !showDrawerIcon })}
                                      role="button"
                                      tabIndex={0}
                                      aria-label="smart-plan"
                                      onClick={(e) => { e.stopPropagation(); handleAccountPlanClick(company?.companyId); }}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <SmartPlanSvg color="#fff" />
                                    </div>
                                  </LazyTooltip>
                                </div>
                              ) : null
                            }
                          </div>
                        </div>
                      </div>
                      {
                        showButton
                          ? (
                            <div
                              className={classNames('d-flex justify-content-center follow-action', { actionTableBtn: isTableCompanyCompare })}
                              role="button"
                              tabIndex={0}
                              aria-label="button"
                              onClick={(e) => e.stopPropagation()}
                              onKeyPress={(e) => e.stopPropagation()}
                            >
                              <CompanyFollowUnFollowButton
                                removeButton={removeButton}
                                companyData={company}
                                isSearch={isSearch}
                              />
                            </div>
                          ) : null
                      }
                    </div>
                  </LazyTooltipBoundary>
                ))
              }
              {!loading && !isDetailedSearch && forceVisible && !companiesCollection.length && (
                <EmptyResults>No Company found</EmptyResults>
              )}
              {shouldShowPaginationSkeleton && <CompanyListViewSkeleton />}
            </div>
          )
        }
      </StyleWrapper>
    ) : (
      null
    )
  );
};
CompaniesSectionListView.defaultProps = defaultProps;
export default CompaniesSectionListView;
